import {
	Alert,
	AlertDescription,
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Checkbox,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Slider,
	Switch,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Textarea,
} from "@ancatag/at-design";
import {
	AlertCircle,
	ArrowLeft,
	Check,
	CheckCircle2,
	ChevronDown,
	ChevronUp,
	Cpu,
	ExternalLink,
	FileText,
	Info,
	Plus,
	Radio,
	Save,
	Settings,
	Sparkles,
	X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useRoute } from "wouter";
import {
	type EmbeddingModelConfig,
	type ModelConfig,
	modelConfigsService,
} from "../../services/models";
import {
	addModelConfigToRouteConfig,
	getRouteConfig,
	type RouteConfig,
	removeModelConfigFromRouteConfig,
	setPrimaryModelConfig,
	testRouteConfigConnection,
	type UpdateRouteConfigData,
	updateModelConfigPriority,
	updateRouteConfig,
} from "../../services/route-configs";

export function EditModelConfig() {
	const [, setLocation] = useLocation();
	const [match, params] = useRoute("/route-configs/:id/edit");
	const configId = params?.id;

	const [config, setConfig] = useState<RouteConfig | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Form state
	const [formData, setFormData] = useState<UpdateRouteConfigData>({
		name: "",
		description: "",
		systemPrompt: "",
		prePromptItems: [],
		hotCacheEnabled: true,
		semanticCacheEnabled: true,
		ragEnabled: false,
		embeddingModelConfigId: undefined,
		isActive: true,
		isNsfw: false,
		nsfwLevel: undefined,
		nsfwWarning: undefined,
		requiresAgeVerification: false,
		responseFormat: undefined,
	});

	// RAG document count
	const [ragDocumentCount, setRagDocumentCount] = useState<number | null>(null);

	// Embedding models
	const [embeddingModels, setEmbeddingModels] = useState<
		EmbeddingModelConfig[]
	>([]);
	const [loadingEmbeddingModels, setLoadingEmbeddingModels] = useState(false);

	// State for managing model configs
	const [availableModelConfigs, setAvailableModelConfigs] = useState<
		ModelConfig[]
	>([]);
	const [loadingModelConfigs, setLoadingModelConfigs] = useState(false);
	const [addingModelConfigs, setAddingModelConfigs] = useState(false);
	const [selectedModelConfigIds, setSelectedModelConfigIds] = useState<
		Set<string>
	>(new Set());
	const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(
		new Set(),
	);

	// Get primary model config for display
	const primaryModelConfig = config?.models?.find(
		(m) => m.isPrimary,
	)?.modelConfig;

	// Check if provider is Ollama (Ollama-specific parameters)
	const isOllama = primaryModelConfig?.providerType === "ollama";

	useEffect(() => {
		// Don't fetch if route doesn't match or configId is missing
		if (!match) {
			setError("Route not found");
			setLoading(false);
			return;
		}

		if (!configId) {
			setError("Invalid config ID");
			setLoading(false);
			return;
		}

		const fetchConfig = async () => {
			try {
				setLoading(true);
				setError(null);
				const data = await getRouteConfig(configId);
				setConfig(data);
				setFormData({
					name: data.name,
					description: data.description || "",
					systemPrompt: data.systemPrompt || "",
					responseFormat: data.responseFormat || undefined,
					prePromptItems: data.prePromptItems || [],
					hotCacheEnabled: data.hotCacheEnabled,
					semanticCacheEnabled: data.semanticCacheEnabled,
					ragEnabled: data.ragEnabled,
					embeddingModelConfigId: data.embeddingModelConfig?.id || undefined,
					isActive: data.isActive,
					parameters: data.parameters || undefined,
					isNsfw: data.isNsfw || false,
					nsfwLevel:
						(data.nsfwLevel as "mild" | "moderate" | "explicit" | undefined) ||
						undefined,
					nsfwWarning: data.nsfwWarning || undefined,
					requiresAgeVerification: data.requiresAgeVerification || false,
				});

				// Fetch RAG document count if RAG is enabled
				if (data.ragEnabled && configId) {
					import("../../services/rag")
						.then(({ ragService }) => {
							ragService
								.getCollection(configId)
								.then((collection) => {
									setRagDocumentCount(collection.documentCount);
								})
								.catch(() => {
									setRagDocumentCount(0);
								});
						})
						.catch(() => {
							setRagDocumentCount(0);
						});
				} else {
					setRagDocumentCount(null);
				}
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Failed to load config";
				setError(errorMessage);
				toast.error(`Failed to load route configuration: ${errorMessage}`);
			} finally {
				setLoading(false);
			}
		};

		fetchConfig();
	}, [match, configId]);

	// Fetch embedding models on mount
	useEffect(() => {
		const fetchEmbeddingModels = async () => {
			try {
				setLoadingEmbeddingModels(true);
				const models = await modelConfigsService.listEmbeddingModels();
				setEmbeddingModels(models);
			} catch (err) {
				console.error("Failed to fetch embedding models:", err);
				toast.error("Failed to load embedding models");
			} finally {
				setLoadingEmbeddingModels(false);
			}
		};

		fetchEmbeddingModels();
	}, []);

	// Fetch available model configs for adding to route config
	useEffect(() => {
		if (!configId || !config) return;

		const fetchAvailableModelConfigs = async () => {
			try {
				setLoadingModelConfigs(true);
				const response = await modelConfigsService.list({
					generationType: config.generationType,
					isActive: true,
					limit: 100,
				});

				// Filter out model configs that are already linked to this route config
				const linkedModelConfigIds = new Set(
					config.models.map((m) => m.modelConfigId),
				);
				const available = response.data.filter(
					(mc) => !linkedModelConfigIds.has(mc.id),
				);
				setAvailableModelConfigs(available);
			} catch (err) {
				console.error("Failed to fetch available model configs:", err);
			} finally {
				setLoadingModelConfigs(false);
			}
		};

		fetchAvailableModelConfigs();
	}, [configId, config]);

	// Handle toggling model config selection
	const handleToggleModelConfig = (modelConfigId: string) => {
		setSelectedModelConfigIds((prev) => {
			const next = new Set(prev);
			if (next.has(modelConfigId)) {
				next.delete(modelConfigId);
			} else {
				next.add(modelConfigId);
			}
			return next;
		});
	};

	// Handle adding selected model configs to route config
	const handleAddSelectedModelConfigs = async () => {
		if (!configId || selectedModelConfigIds.size === 0) {
			toast.error("Please select at least one model config to add");
			return;
		}

		if (addingModelConfigs) {
			return; // Prevent multiple simultaneous requests
		}

		setAddingModelConfigs(true);
		try {
			const currentModelCount = config?.models.length || 0;
			let addedCount = 0;
			let errorCount = 0;

			// Add each selected model config
			const errors: string[] = [];
			for (const modelConfigId of selectedModelConfigIds) {
				try {
					await addModelConfigToRouteConfig(configId, {
						modelConfigId,
						isPrimary: currentModelCount + addedCount === 0, // Make first one primary
						priority: currentModelCount + addedCount,
					});
					addedCount++;
				} catch (err) {
					errorCount++;
					const errorMessage =
						err instanceof Error ? err.message : "Unknown error";
					errors.push(errorMessage);
					console.error(`Failed to add model config ${modelConfigId}:`, err);
				}
			}

			if (addedCount > 0) {
				toast.success(
					`Successfully added ${addedCount} model config${addedCount > 1 ? "s" : ""}`,
				);
			}
			if (errorCount > 0) {
				const errorMsg = errors.length > 0 ? `: ${errors[0]}` : "";
				toast.error(
					`Failed to add ${errorCount} model config${errorCount > 1 ? "s" : ""}${errorMsg}`,
				);
			}

			setSelectedModelConfigIds(new Set());

			// Refresh config to get updated model list
			const updated = await getRouteConfig(configId);
			setConfig(updated);

			// Refresh available model configs
			const response = await modelConfigsService.list({
				generationType: updated.generationType,
				isActive: true,
				limit: 100,
			});
			const linkedModelConfigIds = new Set(
				updated.models.map((m) => m.modelConfigId),
			);
			const available = response.data.filter(
				(mc) => !linkedModelConfigIds.has(mc.id),
			);
			setAvailableModelConfigs(available);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to add model configs";
			toast.error(errorMessage);
			console.error("Error in handleAddSelectedModelConfigs:", err);
		} finally {
			setAddingModelConfigs(false);
		}
	};

	// Handle toggling model config for removal
	const handleToggleForRemoval = (modelConfigId: string) => {
		setSelectedForRemoval((prev) => {
			const next = new Set(prev);
			if (next.has(modelConfigId)) {
				next.delete(modelConfigId);
			} else {
				next.add(modelConfigId);
			}
			return next;
		});
	};

	// Handle removing selected model configs from route config
	const handleRemoveSelectedModelConfigs = async () => {
		if (!configId || selectedForRemoval.size === 0) {
			toast.error("Please select at least one model config to remove");
			return;
		}

		// Warn if removing all models, but allow it
		if (config && config.models.length - selectedForRemoval.size < 1) {
			const confirmed = window.confirm(
				`Are you sure you want to remove all ${selectedForRemoval.size} model(s)? You'll need to add another model before using this route.`,
			);
			if (!confirmed) {
				return;
			}
		}

		try {
			let removedCount = 0;
			let errorCount = 0;

			// Remove each selected model config
			const errors: string[] = [];
			for (const modelConfigId of selectedForRemoval) {
				try {
					await removeModelConfigFromRouteConfig(configId, modelConfigId);
					removedCount++;
				} catch (err) {
					errorCount++;
					const errorMessage =
						err instanceof Error ? err.message : "Unknown error";
					errors.push(errorMessage);
					console.error(`Failed to remove model config ${modelConfigId}:`, err);
				}
			}

			if (removedCount > 0) {
				toast.success(
					`Successfully removed ${removedCount} model config${removedCount > 1 ? "s" : ""}`,
				);
			}
			if (errorCount > 0) {
				const errorMsg = errors.length > 0 ? `: ${errors[0]}` : "";
				toast.error(
					`Failed to remove ${errorCount} model config${errorCount > 1 ? "s" : ""}${errorMsg}`,
				);
			}

			setSelectedForRemoval(new Set());

			// Refresh config
			const updated = await getRouteConfig(configId);
			setConfig(updated);

			// Refresh available model configs
			const response = await modelConfigsService.list({
				generationType: updated.generationType,
				isActive: true,
				limit: 100,
			});
			const linkedModelConfigIds = new Set(
				updated.models.map((m) => m.modelConfigId),
			);
			const available = response.data.filter(
				(mc) => !linkedModelConfigIds.has(mc.id),
			);
			setAvailableModelConfigs(available);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to remove model configs";
			toast.error(errorMessage);
		}
	};

	// Handle removing a single model config from route config
	const handleRemoveModelConfig = async (modelConfigId: string) => {
		if (!configId || !config) return;

		// Find the model to get its name for the toast
		const modelToRemove = config.models.find((m) => {
			const id = m.modelConfigId || m.modelConfig?.id;
			return id === modelConfigId;
		});
		const modelName = modelToRemove?.modelConfig?.name || "model";

		// Warn if removing the last model, but allow it
		if (config.models.length === 1) {
			const confirmed = window.confirm(
				`Are you sure you want to remove "${modelName}"? This is the last model in this route. You'll need to add another model before using this route.`,
			);
			if (!confirmed) {
				return;
			}
		}

		try {
			console.log("Removing model config:", {
				configId,
				modelConfigId,
				modelName,
			});
			await removeModelConfigFromRouteConfig(configId, modelConfigId);
			console.log("Model config removed successfully");
			toast.success(`"${modelName}" removed from route`);

			// Refresh config
			const updated = await getRouteConfig(configId);
			setConfig(updated);

			// Refresh available model configs
			const response = await modelConfigsService.list({
				generationType: updated.generationType,
				isActive: true,
				limit: 100,
			});
			const linkedModelConfigIds = new Set(
				updated.models
					.map((m) => m.modelConfigId || m.modelConfig?.id)
					.filter(Boolean),
			);
			const available = response.data.filter(
				(mc) => !linkedModelConfigIds.has(mc.id),
			);
			setAvailableModelConfigs(available);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to remove model config";
			toast.error(errorMessage);
			console.error("Remove model error:", {
				error: err,
				configId,
				modelConfigId,
				message: err instanceof Error ? err.message : String(err),
				status: (err as any)?.status,
				code: (err as any)?.code,
			});
		}
	};

	// Handle setting primary model config
	const handleSetPrimary = async (modelConfigId: string) => {
		if (!configId || !config) return;

		// Find the model to get its name for the toast
		const modelToSet = config.models.find((m) => {
			const id = m.modelConfigId || m.modelConfig?.id;
			return id === modelConfigId;
		});

		if (!modelToSet) {
			toast.error("Model not found");
			return;
		}

		const modelName = modelToSet.modelConfig?.name || "model";

		try {
			await setPrimaryModelConfig(configId, modelConfigId);
			toast.success(`"${modelName}" set as primary model`);

			// Refresh config
			const updated = await getRouteConfig(configId);
			setConfig(updated);
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: "Failed to set primary model config";
			toast.error(errorMessage);
			console.error("Set primary error:", err);
		}
	};

	// Handle moving priority up/down
	const handleMovePriority = async (
		modelConfigId: string,
		direction: "up" | "down",
	) => {
		if (!configId || !config) return;

		// Find the model link - check both modelConfigId and modelConfig.id
		const modelLink = config.models.find((m) => {
			const id = m.modelConfigId || m.modelConfig?.id;
			return id === modelConfigId;
		});

		if (!modelLink || !modelLink.modelConfig) {
			toast.error("Model not found");
			return;
		}

		const sortedModels = [...config.models]
			.filter((m) => m.modelConfig)
			.sort((a, b) => a.priority - b.priority);

		// Find current index using the same logic
		const currentIndex = sortedModels.findIndex((m) => {
			const id = m.modelConfigId || m.modelConfig?.id;
			return id === modelConfigId;
		});

		if (currentIndex === -1) {
			toast.error("Model not found in sorted list");
			return;
		}

		const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
		if (newIndex < 0 || newIndex >= sortedModels.length) {
			toast.error(
				`Cannot move ${direction === "up" ? "up" : "down"} - already at ${direction === "up" ? "highest" : "lowest"} priority`,
			);
			return;
		}

		const targetModel = sortedModels[newIndex];
		if (!targetModel.modelConfig) {
			toast.error("Target model not found");
			return;
		}

		const currentPriority = modelLink.priority;
		const targetPriority = targetModel.priority;
		const targetModelConfigId =
			targetModel.modelConfigId || targetModel.modelConfig.id;

		// Swap priorities
		try {
			await Promise.all([
				updateModelConfigPriority(configId, modelConfigId, targetPriority),
				updateModelConfigPriority(
					configId,
					targetModelConfigId,
					currentPriority,
				),
			]);
			toast.success(
				`Priority moved ${direction === "up" ? "up" : "down"} (${currentPriority} ↔ ${targetPriority})`,
			);

			// Refresh config
			const updated = await getRouteConfig(configId);
			setConfig(updated);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to update priority";
			toast.error(errorMessage);
			console.error("Priority update error:", err);
		}
	};

	// Fetch available models from provider
	// Note: Currently unused - will be implemented when model management UI is added

	const handleSave = async () => {
		if (!configId || !formData.name?.trim()) {
			toast.error("Name is required");
			return;
		}

		setSaving(true);
		try {
			const updated = await updateRouteConfig(configId, {
				...formData,
				description: formData.description || null,
				parameters:
					formData.parameters && Object.keys(formData.parameters).length > 0
						? formData.parameters
						: undefined,
				systemPrompt: formData.systemPrompt || null,
				prePromptItems:
					formData.prePromptItems && formData.prePromptItems.length > 0
						? formData.prePromptItems
						: null,
			});
			toast.success("Model configuration updated successfully");
			setConfig(updated);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update configuration",
			);
		} finally {
			setSaving(false);
		}
	};

	// Model selection is handled via RouteConfigModelConfig management
	// This is just for display purposes

	const handleTest = async () => {
		if (!configId) return;

		// Check if route config has model configs before testing
		if (!config?.models || config.models.length === 0) {
			toast.error(
				"Route config must have at least one model config. Please add a model config first.",
			);
			return;
		}

		setTesting(true);
		try {
			const result = await testRouteConfigConnection(configId);
			if (result.success) {
				toast.success(`Ping successful! (${result.responseTime}ms)`);
				// Refresh config to get updated status
				const refreshed = await getRouteConfig(configId);
				setConfig(refreshed);
			} else {
				toast.error(`Ping failed: ${result.error || "Unknown error"}`);
			}
		} catch (err: any) {
			// Extract error message from API response
			let errorMessage = "Ping failed";
			if (err instanceof Error) {
				errorMessage = err.message;
			} else if (err?.message) {
				errorMessage = err.message;
			} else if (typeof err === "string") {
				errorMessage = err;
			}

			// Check for specific validation errors
			if (errorMessage.includes("must have at least one model config")) {
				toast.error(
					"Route config must have at least one model config. Please add a model config and set it as primary.",
				);
			} else {
				toast.error(errorMessage);
			}
		} finally {
			setTesting(false);
		}
	};

	// Show error if route doesn't match
	if (!match) {
		return (
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-6">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setLocation("/route-configs")}
						className="h-8 w-8"
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<h1 className="text-3xl font-bold">Edit Route Configuration</h1>
				</div>
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						Invalid route. Please navigate from the models page.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="space-y-4">
					<div className="h-10 w-64 bg-muted animate-pulse rounded" />
					<Card>
						<CardContent className="p-6">
							<div className="space-y-4">
								<div className="h-4 w-full bg-muted animate-pulse rounded" />
								<div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	if (error || !config) {
		return (
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-6">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setLocation("/route-configs")}
						className="h-8 w-8"
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<h1 className="text-3xl font-bold">Edit Route Configuration</h1>
				</div>
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						{error || "Model configuration not found"}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	const status = primaryModelConfig?.connectionStatus || "pending";
	const hasError = status === "error";
	const connectionError = primaryModelConfig?.connectionError;

	return (
		<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
			{/* Header with actions */}
			<div className="flex items-start justify-between mb-6">
				<div className="flex-1">
					<div className="flex items-center gap-3 mb-2">
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setLocation("/route-configs")}
							className="h-8 w-8"
						>
							<ArrowLeft className="h-4 w-4" />
						</Button>
						<div>
							<h1 className="text-3xl font-bold">Edit Route Configuration</h1>
							<p className="text-muted-foreground mt-1">{config.name}</p>
						</div>
					</div>
				</div>
				<div className="flex items-center gap-2 ml-4">
					<Button variant="outline" onClick={handleTest} disabled={testing}>
						<Radio
							className={`h-4 w-4 mr-2 ${testing ? "animate-pulse" : ""}`}
						/>
						{testing ? "Pinging..." : "Ping"}
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						<Save className="h-4 w-4 mr-2" />
						{saving ? "Saving..." : "Save Changes"}
					</Button>
				</div>
			</div>

			{/* Error/Status Alerts */}
			{hasError && connectionError && (
				<Alert variant="destructive" className="mb-6">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						<div className="font-medium mb-1">Connection Error</div>
						<div className="text-sm">{connectionError}</div>
						<div className="text-xs mt-2 opacity-80">
							Update credentials or check your provider settings, then ping the
							connection again.
						</div>
					</AlertDescription>
				</Alert>
			)}

			{status === "connected" && (
				<Alert className="mb-6 border-green-500/20 bg-green-500/5">
					<CheckCircle2 className="h-4 w-4 text-green-600" />
					<AlertDescription className="text-green-400">
						Connection is healthy
					</AlertDescription>
				</Alert>
			)}

			<Tabs defaultValue="overview" className="w-full">
				<TabsList className="grid w-full grid-cols-4 mb-6">
					<TabsTrigger value="overview" className="flex items-center gap-2">
						<Info className="h-4 w-4" />
						Overview
					</TabsTrigger>
					<TabsTrigger value="models" className="flex items-center gap-2">
						<Cpu className="h-4 w-4" />
						Models
					</TabsTrigger>
					<TabsTrigger value="generation" className="flex items-center gap-2">
						<Sparkles className="h-4 w-4" />
						Generation
					</TabsTrigger>
					<TabsTrigger value="advanced" className="flex items-center gap-2">
						<Settings className="h-4 w-4" />
						Advanced
					</TabsTrigger>
				</TabsList>

				<TabsContent value="overview" className="space-y-6">
					<div className="grid gap-6 md:grid-cols-2">
						{/* Basic Info */}
						<Card>
							<CardHeader className="pb-4">
								<CardTitle>Basic Information</CardTitle>
								<CardDescription>
									Name and description for this configuration
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-5">
								<div className="space-y-2">
									<Label htmlFor="name">Name *</Label>
									<Input
										id="name"
										value={formData.name || ""}
										onChange={(e) =>
											setFormData({ ...formData, name: e.target.value })
										}
										placeholder="My Chat Model"
										className="w-full"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="description">Description</Label>
									<Textarea
										id="description"
										value={formData.description || ""}
										onChange={(e) =>
											setFormData({ ...formData, description: e.target.value })
										}
										placeholder="Optional description..."
										rows={3}
										className="w-full"
									/>
								</div>
								<div className="flex items-center justify-between pt-2 border-t">
									<div>
										<Label>Active</Label>
										<p className="text-xs text-muted-foreground">
											Enable this configuration
										</p>
									</div>
									<Switch
										checked={formData.isActive ?? true}
										onCheckedChange={(v) =>
											setFormData({ ...formData, isActive: v })
										}
									/>
								</div>
							</CardContent>
						</Card>

						{/* Provider & Model */}
						<Card>
							<CardHeader className="pb-4">
								<CardTitle>Provider & Model</CardTitle>
								<CardDescription>Provider and model selection</CardDescription>
							</CardHeader>
							<CardContent className="space-y-5">
								<div className="space-y-2">
									<Label>Provider</Label>
									<div>
										<Badge
											variant="outline"
											className="capitalize text-sm px-3 py-1"
										>
											{primaryModelConfig?.providerType || "N/A"}
										</Badge>
									</div>
								</div>
								<div className="space-y-2">
									<Label>Model</Label>
									<div>
										<Badge variant="outline" className="text-sm px-3 py-1">
											{primaryModelConfig?.modelDisplayName ||
												primaryModelConfig?.modelIdentifier ||
												"No model selected"}
										</Badge>
									</div>
								</div>
								<div className="space-y-2">
									<Label>Generation Type</Label>
									<div>
										<Badge
											variant="outline"
											className="capitalize text-sm px-3 py-1"
										>
											{config.generationType}
										</Badge>
									</div>
								</div>
								<div className="space-y-2">
									<Label>Mode</Label>
									<div>
										<Badge
											variant={
												primaryModelConfig?.isHosted ? "default" : "secondary"
											}
											className="text-sm px-3 py-1"
										>
											{primaryModelConfig?.isHosted ? "Hosted" : "BYOP"}
										</Badge>
									</div>
								</div>
								<div className="space-y-2">
									<Label>Connection Status</Label>
									<div>
										<Badge
											variant="outline"
											className={`text-sm px-3 py-1 ${
												status === "connected"
													? "border-green-500 text-green-600"
													: status === "error"
														? "border-red-500 text-red-600"
														: "border-yellow-500 text-yellow-600"
											}`}
										>
											{status === "connected"
												? "Connected"
												: status === "error"
													? "Error"
													: "Pending"}
										</Badge>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</TabsContent>

				<TabsContent value="models" className="space-y-6">
					{/* Model Configs Management - Full width */}
					<Card>
						<CardHeader className="pb-4">
							<CardTitle>Route Model Configuration</CardTitle>
							<CardDescription>
								Add models to this route configuration and set their priority
								order. The primary model is used for health checks, and models
								are selected based on priority when the primary fails.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							{/* Info Alert */}
							<Alert>
								<Info className="h-4 w-4" />
								<AlertDescription>
									<div className="font-medium mb-1">
										How Model Priority Works
									</div>
									<div className="text-sm space-y-1">
										<p>
											• <strong>Primary Model:</strong> Used for health checks
											and as the default model
										</p>
										<p>
											• <strong>Priority Order:</strong> Lower numbers = higher
											priority (0 is highest)
										</p>
										<p>
											• <strong>Fallback:</strong> If the primary fails, models
											are tried in priority order
										</p>
										<p>
											• <strong>Multiple Models:</strong> Add multiple models
											for redundancy and load balancing
										</p>
									</div>
								</AlertDescription>
							</Alert>
							{/* Add Model Configs (Multi-Select) */}
							<div className="space-y-4">
								<div className="space-y-1.5">
									<Label>Add Models to Route</Label>
									<p className="text-xs text-muted-foreground">
										Select models to add to this route configuration. They will
										be assigned priority automatically.
									</p>
								</div>
								{availableModelConfigs.length > 0 && (
									<div className="flex flex-wrap items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => {
												if (
													selectedModelConfigIds.size ===
													availableModelConfigs.length
												) {
													setSelectedModelConfigIds(new Set());
												} else {
													setSelectedModelConfigIds(
														new Set(availableModelConfigs.map((mc) => mc.id)),
													);
												}
											}}
											disabled={loadingModelConfigs}
										>
											{selectedModelConfigIds.size ===
											availableModelConfigs.length
												? "Deselect All"
												: "Select All"}
										</Button>
										{selectedModelConfigIds.size > 0 && (
											<>
												<Button
													variant="outline"
													size="sm"
													onClick={() => setSelectedModelConfigIds(new Set())}
												>
													Clear
												</Button>
												<Button
													onClick={handleAddSelectedModelConfigs}
													disabled={loadingModelConfigs || addingModelConfigs}
													size="sm"
												>
													<Plus
														className={`h-4 w-4 mr-2 ${addingModelConfigs ? "animate-spin" : ""}`}
													/>
													{addingModelConfigs
														? "Adding..."
														: `Add Selected (${selectedModelConfigIds.size})`}
												</Button>
											</>
										)}
									</div>
								)}

								{loadingModelConfigs ? (
									<div className="text-sm text-muted-foreground py-4 text-center">
										Loading model configs...
									</div>
								) : availableModelConfigs.length === 0 ? (
									<p className="text-xs text-muted-foreground">
										No available model configs. Create a model config first that
										matches the generation type ({config.generationType}).
									</p>
								) : (
									<div className="border rounded-lg max-h-60 overflow-y-auto">
										{availableModelConfigs.map((mc) => {
											const isSelected = selectedModelConfigIds.has(mc.id);
											return (
												<div
													key={mc.id}
													className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer"
													onClick={() => handleToggleModelConfig(mc.id)}
												>
													<Checkbox
														checked={isSelected}
														onCheckedChange={() =>
															handleToggleModelConfig(mc.id)
														}
														onClick={(e) => e.stopPropagation()}
														onPointerDown={(e) => e.stopPropagation()}
													/>
													<div className="flex-1 min-w-0">
														<div className="font-medium text-sm">{mc.name}</div>
														<div className="text-xs text-muted-foreground">
															{mc.providerType} • {mc.modelIdentifier}
														</div>
														<div className="flex items-center gap-2 mt-1">
															<Badge
																variant="outline"
																className={
																	mc.connectionStatus === "connected"
																		? "border-green-500 text-green-600 text-xs"
																		: mc.connectionStatus === "error"
																			? "border-red-500 text-red-600 text-xs"
																			: "border-yellow-500 text-yellow-600 text-xs"
																}
															>
																{mc.connectionStatus}
															</Badge>
															{mc.isHosted ? (
																<Badge variant="default" className="text-xs">
																	Hosted
																</Badge>
															) : (
																<Badge variant="secondary" className="text-xs">
																	BYOP
																</Badge>
															)}
														</div>
													</div>
													{isSelected && (
														<Check className="h-4 w-4 text-primary" />
													)}
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* Current Model Configs */}
							<div className="space-y-4 border-t pt-6">
								<div className="flex items-center justify-between">
									<div className="space-y-1.5">
										<Label>
											Models in This Route (
											{config.models.filter((m) => m.modelConfig).length})
										</Label>
										<p className="text-xs text-muted-foreground">
											Manage models and their priority order. Drag or use arrows
											to reorder.
										</p>
									</div>
									{config.models.filter((m) => m.modelConfig).length > 0 && (
										<div className="flex items-center gap-2">
											{selectedForRemoval.size > 0 && (
												<>
													<Button
														variant="outline"
														size="sm"
														onClick={() => setSelectedForRemoval(new Set())}
													>
														Clear
													</Button>
													<Button
														variant="destructive"
														size="sm"
														onClick={handleRemoveSelectedModelConfigs}
														disabled={
															config.models.length - selectedForRemoval.size < 1
														}
													>
														<X className="h-4 w-4 mr-2" />
														Remove Selected ({selectedForRemoval.size})
													</Button>
												</>
											)}
										</div>
									)}
								</div>
								{config.models.filter((m) => m.modelConfig).length === 0 ? (
									<div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
										No models added to this route. Add models above to get
										started.
									</div>
								) : (
									<div className="space-y-3">
										{config.models
											.filter((modelLink) => modelLink.modelConfig) // Filter out entries with missing modelConfig
											.sort((a, b) => a.priority - b.priority)
											.map((modelLink, index, sortedArray) => {
												const mc = modelLink.modelConfig;
												if (!mc) return null; // Additional safety check
												const modelConfigId = modelLink.modelConfigId || mc.id; // Use link's modelConfigId, fallback to model's id
												const isSelectedForRemoval =
													selectedForRemoval.has(modelConfigId);
												const canMoveUp = index > 0;
												const canMoveDown = index < sortedArray.length - 1;

												return (
													<div
														key={modelLink.id}
														className={`flex items-center gap-4 p-4 border rounded-lg transition-colors relative ${
															isSelectedForRemoval
																? "bg-destructive/10 border-destructive/50"
																: modelLink.isPrimary
																	? "bg-primary/5 border-primary/20"
																	: "hover:bg-muted/50"
														}`}
														onClick={(e) => {
															// Allow clicks on the container for selection, but don't interfere with button clicks
															const target = e.target as HTMLElement;
															if (
																target.closest("button") ||
																target.closest('[role="button"]')
															) {
																return; // Let button handle its own click
															}
														}}
													>
														<Checkbox
															checked={isSelectedForRemoval}
															onCheckedChange={() => {
																handleToggleForRemoval(modelConfigId);
															}}
															onClick={(e) => e.stopPropagation()}
															className="mt-1 flex-shrink-0"
														/>

														{/* Priority Number Badge */}
														<div className="flex flex-col items-center gap-1">
															<Badge
																variant={
																	modelLink.isPrimary ? "default" : "outline"
																}
																className="w-8 h-8 rounded-full flex items-center justify-center p-0 text-xs font-semibold"
															>
																{modelLink.priority}
															</Badge>
															{modelLink.isPrimary && (
																<span className="text-[10px] text-muted-foreground">
																	Primary
																</span>
															)}
														</div>

														{/* Priority Controls */}
														<div className="flex flex-col gap-1">
															<Button
																variant="ghost"
																size="icon"
																className="h-6 w-6"
																onClick={() =>
																	handleMovePriority(modelConfigId, "up")
																}
																disabled={!canMoveUp}
																title={`Move to priority ${modelLink.priority > 0 ? modelLink.priority - 1 : 0} (higher priority)`}
															>
																<ChevronUp className="h-3 w-3" />
															</Button>
															<Button
																variant="ghost"
																size="icon"
																className="h-6 w-6"
																onClick={() =>
																	handleMovePriority(modelConfigId, "down")
																}
																disabled={!canMoveDown}
																title={`Move to priority ${modelLink.priority + 1} (lower priority)`}
															>
																<ChevronDown className="h-3 w-3" />
															</Button>
														</div>

														{/* Model Info */}
														<div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
															<div className="flex-1 min-w-0 overflow-hidden">
																<div className="font-medium flex items-center gap-2">
																	{mc.name}
																	{modelLink.isPrimary && (
																		<Badge
																			variant="default"
																			className="text-xs"
																		>
																			Primary
																		</Badge>
																	)}
																</div>
																<div className="text-xs text-muted-foreground mt-0.5">
																	{mc.providerType} • {mc.modelIdentifier}
																</div>
																<div className="flex items-center gap-2 mt-1.5">
																	<Badge
																		variant="outline"
																		className={
																			mc.connectionStatus === "connected"
																				? "border-green-500 text-green-600 text-xs"
																				: mc.connectionStatus === "error"
																					? "border-red-500 text-red-600 text-xs"
																					: "border-yellow-500 text-yellow-600 text-xs"
																		}
																	>
																		{mc.connectionStatus}
																	</Badge>
																	{mc.isHosted ? (
																		<Badge
																			variant="default"
																			className="text-xs"
																		>
																			Hosted
																		</Badge>
																	) : (
																		<Badge
																			variant="secondary"
																			className="text-xs"
																		>
																			BYOP
																		</Badge>
																	)}
																</div>
															</div>
														</div>

														{/* Actions */}
														<div className="flex items-center gap-2 flex-shrink-0 relative z-20">
															{!modelLink.isPrimary && (
																<Button
																	variant="outline"
																	size="sm"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleSetPrimary(modelConfigId);
																	}}
																	title="Set as primary model (used for health checks)"
																>
																	Set Primary
																</Button>
															)}
															<Button
																type="button"
																variant="ghost"
																size="icon"
																onClick={(e) => {
																	e.stopPropagation();
																	e.preventDefault();
																	console.log("Delete button clicked", {
																		modelConfigId,
																		modelCount: config.models.length,
																	});
																	handleRemoveModelConfig(modelConfigId);
																}}
																onMouseDown={(e) => {
																	e.stopPropagation();
																}}
																title="Remove model from route"
																className="text-destructive hover:text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed relative z-30 pointer-events-auto"
																aria-label="Remove model from route"
															>
																<X className="h-4 w-4 pointer-events-none" />
															</Button>
														</div>
													</div>
												);
											})
											.filter(Boolean)}
									</div>
								)}
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="generation" className="space-y-6">
					{/* Generation Parameters - Full width */}
					<Card>
						<CardHeader className="pb-4">
							<CardTitle>Generation Parameters</CardTitle>
							<CardDescription>
								Configure how the model generates responses
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="space-y-3">
								<div className="flex items-center justify-between">
									<Label htmlFor="temperature" className="text-sm font-medium">
										Temperature:{" "}
										<span className="font-semibold">
											{(formData.parameters as any)?.temperature ??
												config.parameters?.temperature ??
												0.7}
										</span>
									</Label>
									<span className="text-xs text-muted-foreground">
										0.0 - 2.0
									</span>
								</div>
								<Slider
									id="temperature"
									min={0}
									max={2}
									step={0.1}
									value={[
										(formData.parameters as any)?.temperature ??
											config.parameters?.temperature ??
											0.7,
									]}
									onValueChange={([value]) =>
										setFormData({
											...formData,
											parameters: {
												...((formData.parameters as any) ||
													config.parameters ||
													{}),
												temperature: value,
											},
										})
									}
								/>
								<p className="text-xs text-muted-foreground">
									Controls randomness. Lower = more focused, Higher = more
									creative
								</p>
							</div>

							<div className="space-y-3">
								<Label htmlFor="maxTokens" className="text-sm font-medium">
									Max Tokens
								</Label>
								<Input
									id="maxTokens"
									type="number"
									min={1}
									max={100000}
									value={
										(formData.parameters as any)?.maxTokens ??
										config.parameters?.maxTokens ??
										""
									}
									onChange={(e) =>
										setFormData({
											...formData,
											parameters: {
												...((formData.parameters as any) ||
													config.parameters ||
													{}),
												maxTokens: e.target.value
													? parseInt(e.target.value, 10)
													: undefined,
											},
										})
									}
								/>
								<p className="text-xs text-muted-foreground">
									Maximum number of tokens to generate (leave empty for model
									default)
								</p>
							</div>

							<div className="space-y-3">
								<div className="flex items-center justify-between">
									<Label htmlFor="topP" className="text-sm font-medium">
										Top P:{" "}
										<span className="font-semibold">
											{(formData.parameters as any)?.topP ??
												config.parameters?.topP ??
												0.95}
										</span>
									</Label>
									<span className="text-xs text-muted-foreground">
										0.0 - 1.0
									</span>
								</div>
								<Slider
									id="topP"
									min={0}
									max={1}
									step={0.01}
									value={[
										(formData.parameters as any)?.topP ??
											config.parameters?.topP ??
											0.95,
									]}
									onValueChange={([value]) =>
										setFormData({
											...formData,
											parameters: {
												...((formData.parameters as any) ||
													config.parameters ||
													{}),
												topP: value,
											},
										})
									}
								/>
								<p className="text-xs text-muted-foreground">
									Nucleus sampling: Controls diversity of vocabulary (higher =
									more diverse)
								</p>
							</div>

							{/* Ollama-specific parameters */}
							{isOllama && (
								<>
									<div className="space-y-3">
										<Label htmlFor="topK" className="text-sm font-medium">
											Top K
										</Label>
										<Input
											id="topK"
											type="number"
											min={1}
											max={100}
											value={
												(formData.parameters as any)?.topK ??
												config.parameters?.topK ??
												""
											}
											onChange={(e) =>
												setFormData({
													...formData,
													parameters: {
														...((formData.parameters as any) ||
															config.parameters ||
															{}),
														topK: e.target.value
															? parseInt(e.target.value, 10)
															: undefined,
													},
												})
											}
										/>
										<p className="text-xs text-muted-foreground">
											Limits vocabulary to top K most likely tokens (1-100,
											leave empty for default)
										</p>
									</div>

									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<Label
												htmlFor="repeatPenalty"
												className="text-sm font-medium"
											>
												Repeat Penalty:{" "}
												<span className="font-semibold">
													{(formData.parameters as any)?.repeatPenalty ??
														config.parameters?.repeatPenalty ??
														1.1}
												</span>
											</Label>
											<span className="text-xs text-muted-foreground">
												1.0 - 2.0
											</span>
										</div>
										<Slider
											id="repeatPenalty"
											min={1.0}
											max={2.0}
											step={0.05}
											value={[
												(formData.parameters as any)?.repeatPenalty ??
													config.parameters?.repeatPenalty ??
													1.1,
											]}
											onValueChange={([value]) =>
												setFormData({
													...formData,
													parameters: {
														...((formData.parameters as any) ||
															config.parameters ||
															{}),
														repeatPenalty: value,
													},
												})
											}
										/>
										<p className="text-xs text-muted-foreground">
											Reduces repetitive phrases (higher = less repetition)
										</p>
									</div>

									<div className="space-y-3">
										<Label
											htmlFor="contextWindow"
											className="text-sm font-medium"
										>
											Context Window
										</Label>
										<Input
											id="contextWindow"
											type="number"
											min={1024}
											max={32768}
											step={1024}
											value={
												formData.parameters?.contextWindow ??
												config.parameters?.contextWindow ??
												""
											}
											onChange={(e) =>
												setFormData({
													...formData,
													parameters: {
														...((formData.parameters as any) ||
															config.parameters ||
															{}),
														...(formData.parameters || config.parameters || {}),
														contextWindow: e.target.value
															? parseInt(e.target.value, 10)
															: undefined,
													},
												})
											}
										/>
										<p className="text-xs text-muted-foreground">
											Maximum tokens for conversation history (1024-32768, leave
											empty for default 8192)
										</p>
									</div>

									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<Label className="text-sm font-medium">
												Stop Sequences
											</Label>
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => {
													const current =
														formData.parameters?.stopSequences ??
														config.parameters?.stopSequences ??
														[];
													setFormData({
														...formData,
														parameters: {
															...(formData.parameters ||
																config.parameters ||
																{}),
															stopSequences: [...current, ""],
														},
													});
												}}
											>
												<Plus className="h-4 w-4 mr-1" />
												Add Sequence
											</Button>
										</div>
										<p className="text-xs text-muted-foreground mb-2">
											Tokens that stop generation when encountered (e.g.,
											"&lt;|im_end|&gt;")
										</p>
										{(!formData.parameters?.stopSequences &&
											!config.parameters?.stopSequences) ||
										(
											formData.parameters?.stopSequences ??
											config.parameters?.stopSequences ??
											[]
										).length === 0 ? (
											<div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
												No stop sequences. Click "Add Sequence" to add one.
											</div>
										) : (
											<div className="space-y-2">
												{(
													formData.parameters?.stopSequences ??
													config.parameters?.stopSequences ??
													[]
												).map((seq: string, index: number) => (
													<div key={index} className="flex gap-2">
														<Input
															placeholder="Stop sequence (e.g., &lt;|im_end|&gt;)"
															value={seq}
															onChange={(e) => {
																const updated = [
																	...(formData.parameters?.stopSequences ??
																		config.parameters?.stopSequences ??
																		[]),
																];
																updated[index] = e.target.value;
																setFormData({
																	...formData,
																	parameters: {
																		...(formData.parameters ||
																			config.parameters ||
																			{}),
																		stopSequences: updated,
																	},
																});
															}}
														/>
														<Button
															type="button"
															variant="ghost"
															size="icon"
															onClick={() => {
																const current =
																	formData.parameters?.stopSequences ??
																	config.parameters?.stopSequences ??
																	[];
																const updated = current.filter(
																	(_: string, i: number) => i !== index,
																);
																setFormData({
																	...formData,
																	parameters: {
																		...(formData.parameters ||
																			config.parameters ||
																			{}),
																		stopSequences:
																			updated.length > 0 ? updated : undefined,
																	},
																});
															}}
														>
															<X className="h-4 w-4" />
														</Button>
													</div>
												))}
											</div>
										)}
									</div>
								</>
							)}
						</CardContent>
					</Card>

					{/* Prompts - Full width */}
					<Card>
						<CardHeader className="pb-4">
							<CardTitle>Prompts</CardTitle>
							<CardDescription>
								System prompt and pre-prompt items
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<div className="space-y-2">
								<Label htmlFor="systemPrompt">System Prompt</Label>
								<Textarea
									id="systemPrompt"
									value={formData.systemPrompt || ""}
									onChange={(e) =>
										setFormData({ ...formData, systemPrompt: e.target.value })
									}
									placeholder="Optional default system prompt..."
									rows={4}
									className="w-full"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="prePromptItems">
									Pre-Prompt Items (one per line)
								</Label>
								<Textarea
									id="prePromptItems"
									value={(formData.prePromptItems || []).join("\n")}
									onChange={(e) =>
										setFormData({
											...formData,
											prePromptItems: e.target.value
												.split("\n")
												.filter((l) => l.trim()),
										})
									}
									placeholder="Context: ...&#10;Remember: ..."
									rows={4}
									className="w-full"
								/>
								<p className="text-xs text-muted-foreground">
									Each line will be prepended to user messages
								</p>
							</div>
						</CardContent>
					</Card>

					{/* Response Format */}
					<Card>
						<CardHeader className="pb-4">
							<CardTitle>Response Format</CardTitle>
							<CardDescription>
								Configure structured JSON output format for classification, data
								extraction, and API integration
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<div className="space-y-2">
								<Label>Format Type</Label>
								<select
									className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
									value={formData.responseFormat?.type || "none"}
									onChange={(e) => {
										if (e.target.value === "none") {
											setFormData({ ...formData, responseFormat: undefined });
										} else {
											setFormData({
												...formData,
												responseFormat: {
													type: e.target.value,
													...(e.target.value === "classification" && {
														categories: [],
														returnConfidence: true,
														returnReason: false,
													}),
													...(e.target.value === "structured" && {
														schema: {
															type: "object",
															properties: {},
															required: [],
														},
													}),
													...(e.target.value === "custom" && {
														format: "",
													}),
												},
											});
										}
									}}
								>
									<option value="none">None (Default)</option>
									<option value="json_object">JSON Object</option>
									<option value="classification">Classification</option>
									<option value="structured">Structured Schema</option>
									<option value="custom">Custom Format</option>
								</select>
								<p className="text-xs text-muted-foreground">
									Select a format type to enforce structured responses
								</p>
							</div>

							{formData.responseFormat?.type === "classification" && (
								<div className="space-y-4 p-4 border rounded-lg bg-muted/50">
									<div className="space-y-2">
										<Label>Categories (one per line)</Label>
										<Textarea
											placeholder="nsfw&#10;spam&#10;toxicity"
											value={(formData.responseFormat?.categories || []).join(
												"\n",
											)}
											onChange={(e) => {
												const categories = e.target.value
													.split("\n")
													.map((c) => c.trim())
													.filter((c) => c.length > 0);
												setFormData({
													...formData,
													responseFormat: {
														...formData.responseFormat,
														categories,
													},
												});
											}}
											rows={4}
										/>
										<p className="text-xs text-muted-foreground">
											Enter category names, one per line
										</p>
									</div>
									<div className="flex items-center space-x-2">
										<input
											type="checkbox"
											id="returnConfidence"
											checked={
												formData.responseFormat?.returnConfidence !== false
											}
											onChange={(e) => {
												setFormData({
													...formData,
													responseFormat: {
														...formData.responseFormat,
														returnConfidence: e.target.checked,
													},
												});
											}}
											className="rounded border-gray-300"
										/>
										<Label
											htmlFor="returnConfidence"
											className="cursor-pointer"
										>
											Return confidence scores
										</Label>
									</div>
									<div className="flex items-center space-x-2">
										<input
											type="checkbox"
											id="returnReason"
											checked={formData.responseFormat?.returnReason || false}
											onChange={(e) => {
												setFormData({
													...formData,
													responseFormat: {
														...formData.responseFormat,
														returnReason: e.target.checked,
													},
												});
											}}
											className="rounded border-gray-300"
										/>
										<Label htmlFor="returnReason" className="cursor-pointer">
											Return reason/explanation
										</Label>
									</div>
								</div>
							)}

							{formData.responseFormat?.type === "structured" && (
								<div className="space-y-2 p-4 border rounded-lg bg-muted/50">
									<Label>JSON Schema</Label>
									<Textarea
										placeholder='{"type": "object", "properties": {...}, "required": [...]}'
										value={JSON.stringify(
											formData.responseFormat?.schema || {},
											null,
											2,
										)}
										onChange={(e) => {
											try {
												const schema = JSON.parse(e.target.value);
												setFormData({
													...formData,
													responseFormat: {
														...formData.responseFormat,
														schema,
													},
												});
											} catch {
												// Invalid JSON, ignore
											}
										}}
										rows={8}
										className="font-mono text-sm"
									/>
									<p className="text-xs text-muted-foreground">
										Enter a valid JSON Schema to enforce response structure
									</p>
								</div>
							)}

							{formData.responseFormat?.type === "custom" && (
								<div className="space-y-2 p-4 border rounded-lg bg-muted/50">
									<Label>Custom Format</Label>
									<Input
										placeholder="csv, xml, etc."
										value={formData.responseFormat?.format || ""}
										onChange={(e) => {
											setFormData({
												...formData,
												responseFormat: {
													...formData.responseFormat,
													format: e.target.value,
												},
											});
										}}
									/>
									<p className="text-xs text-muted-foreground">
										Specify the custom format type
									</p>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="advanced" className="space-y-6">
					{/* Cache & RAG Settings - Full width */}
					<Card>
						<CardHeader className="pb-4">
							<CardTitle>Cache & RAG</CardTitle>
							<CardDescription>Optimization settings</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="flex items-center justify-between py-2">
								<div className="space-y-0.5">
									<Label>Hot Cache</Label>
									<p className="text-xs text-muted-foreground">
										Enable hot caching
									</p>
								</div>
								<Switch
									checked={formData.hotCacheEnabled ?? true}
									onCheckedChange={(v) =>
										setFormData({ ...formData, hotCacheEnabled: v })
									}
								/>
							</div>
							<div className="flex items-center justify-between py-2">
								<div className="space-y-0.5">
									<Label>Semantic Cache</Label>
									<p className="text-xs text-muted-foreground">
										Enable semantic caching
									</p>
								</div>
								<Switch
									checked={formData.semanticCacheEnabled ?? true}
									onCheckedChange={(v) =>
										setFormData({ ...formData, semanticCacheEnabled: v })
									}
								/>
							</div>
							<div className="border-t pt-6 space-y-6">
								<div className="flex items-center justify-between py-2">
									<div className="space-y-0.5">
										<Label>RAG</Label>
										<p className="text-xs text-muted-foreground">
											Enable RAG (Retrieval Augmented Generation)
										</p>
									</div>
									<Switch
										checked={formData.ragEnabled ?? false}
										onCheckedChange={(v) => {
											setFormData({ ...formData, ragEnabled: v });
											if (v && configId) {
												// Fetch document count when enabling RAG
												import("../../services/rag")
													.then(({ ragService }) => {
														ragService
															.getCollection(configId)
															.then((collection) => {
																setRagDocumentCount(collection.documentCount);
															})
															.catch(() => {
																setRagDocumentCount(0);
															});
													})
													.catch(() => {
														setRagDocumentCount(0);
													});
											} else {
												setRagDocumentCount(null);
											}
										}}
									/>
								</div>

								{formData.ragEnabled && (
									<>
										{ragDocumentCount !== null && ragDocumentCount === 0 && (
											<Alert>
												<AlertCircle className="h-4 w-4" />
												<AlertDescription>
													RAG is enabled but no documents have been uploaded.
													Upload documents to enable retrieval.
												</AlertDescription>
											</Alert>
										)}

										<div className="space-y-5 pt-2">
											<div className="flex items-center justify-between gap-4">
												<div className="flex-1 space-y-1.5">
													<Label>Manage Documents</Label>
													<p className="text-xs text-muted-foreground">
														Upload and manage documents for RAG retrieval
														{ragDocumentCount !== null &&
															ragDocumentCount > 0 && (
																<Badge variant="secondary" className="ml-2">
																	{ragDocumentCount} document
																	{ragDocumentCount === 1 ? "" : "s"}
																</Badge>
															)}
													</p>
												</div>
												<Button
													variant="outline"
													size="sm"
													onClick={() =>
														setLocation(`/route-configs/${configId}/rag`)
													}
												>
													<FileText className="h-4 w-4 mr-2" />
													Manage Documents
													<ExternalLink className="h-3 w-3 ml-2" />
												</Button>
											</div>

											<div className="space-y-2">
												<Label htmlFor="embeddingModelConfig">
													Embedding Model
												</Label>
												<Select
													value={formData.embeddingModelConfigId || "__none__"}
													onValueChange={(value) => {
														setFormData({
															...formData,
															embeddingModelConfigId:
																value === "__none__" ? undefined : value,
														});
													}}
													disabled={loadingEmbeddingModels}
												>
													<SelectTrigger id="embeddingModelConfig">
														<SelectValue
															placeholder={
																loadingEmbeddingModels
																	? "Loading embedding models..."
																	: "Select embedding model (optional)"
															}
														/>
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="__none__">
															None (disabled)
														</SelectItem>
														{embeddingModels.map((model) => (
															<SelectItem key={model.id} value={model.id}>
																<div className="flex items-center gap-2">
																	<span>{model.name}</span>
																	{model.isSystem && (
																		<Badge
																			variant="secondary"
																			className="text-xs"
																		>
																			System
																		</Badge>
																	)}
																	<span className="text-muted-foreground text-xs">
																		({model.providerType} -{" "}
																		{model.modelIdentifier})
																	</span>
																</div>
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<p className="text-xs text-muted-foreground">
													Select an embedding model for RAG. Each embedding
													model has its own credentials and is independent from
													your RouteConfig's main models.
												</p>
											</div>

											<Alert>
												<Info className="h-4 w-4" />
												<AlertDescription>
													<div className="font-medium mb-1">How RAG Works</div>
													<div className="text-sm space-y-1">
														<p>
															• Upload documents (PDF, TXT, MD) to build your
															knowledge base
														</p>
														<p>
															• Documents are automatically chunked and embedded
														</p>
														<p>
															• During generation, relevant chunks are retrieved
															and injected into the prompt
														</p>
														<p>
															• This reduces token costs and improves accuracy
															with your specific knowledge
														</p>
													</div>
												</AlertDescription>
											</Alert>
										</div>
									</>
								)}
							</div>
						</CardContent>
					</Card>

					{/* NSFW Compliance */}
					<Card>
						<CardHeader className="pb-4">
							<CardTitle>NSFW Compliance</CardTitle>
							<CardDescription>
								Mark this model as NSFW and configure compliance settings.
								Consumer applications should enforce age verification.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							{formData.isNsfw && (
								<Alert>
									<AlertCircle className="h-4 w-4" />
									<AlertDescription>
										This model is marked as NSFW. Ensure your consumer
										application enforces age verification (18+) before allowing
										access.
									</AlertDescription>
								</Alert>
							)}
							<div className="flex items-center justify-between py-2">
								<div className="space-y-0.5">
									<Label>NSFW Content</Label>
									<p className="text-xs text-muted-foreground">
										Mark this model as containing NSFW content
									</p>
								</div>
								<Switch
									checked={formData.isNsfw ?? false}
									onCheckedChange={(v) =>
										setFormData({ ...formData, isNsfw: v })
									}
								/>
							</div>
							{formData.isNsfw && (
								<div className="space-y-5 border-t pt-6">
									<div className="space-y-2">
										<Label>NSFW Level</Label>
										<Select
											value={formData.nsfwLevel || ""}
											onValueChange={(value) =>
												setFormData({
													...formData,
													nsfwLevel:
														(value as
															| "mild"
															| "moderate"
															| "explicit"
															| undefined) || undefined,
												})
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select NSFW level" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="mild">
													Mild - Suggestive, romantic, flirting
												</SelectItem>
												<SelectItem value="moderate">
													Moderate - Sensual, partial nudity, erotic text
												</SelectItem>
												<SelectItem value="explicit">
													Explicit - Full nudity, explicit sexual content
												</SelectItem>
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Optional: Specify the level of NSFW content
										</p>
									</div>
									<div className="space-y-2">
										<Label>Custom Warning Message</Label>
										<Textarea
											value={formData.nsfwWarning || ""}
											onChange={(e) =>
												setFormData({
													...formData,
													nsfwWarning: e.target.value || undefined,
												})
											}
											placeholder="This model may generate mature content. Ensure your application enforces age verification."
											maxLength={500}
											rows={3}
										/>
										<p className="text-xs text-muted-foreground">
											Optional: Custom warning message for consumer applications
											(max 500 characters)
										</p>
									</div>
									<div className="flex items-center justify-between py-2">
										<div className="space-y-0.5">
											<Label>Requires Age Verification</Label>
											<p className="text-xs text-muted-foreground">
												Recommend age verification for consumer applications
											</p>
										</div>
										<Switch
											checked={formData.requiresAgeVerification ?? false}
											onCheckedChange={(v) =>
												setFormData({ ...formData, requiresAgeVerification: v })
											}
										/>
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</div>
	);
}
